import React, { useState, useEffect } from 'react';
import pointsService from '../../services/pointsService';

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    points: '',
    maxUses: '',
    expiresAt: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const response = await pointsService.getCoupons();
      if (response.data.success) {
        setCoupons(response.data.coupons);
      }
    } catch (error) {
      setError('Fehler beim Laden der Gutscheine');
      console.error('Error loading coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');
    setSuccess('');

    try {
      const couponData = {
        code: formData.code.toUpperCase(),
        points: parseInt(formData.points),
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        expiresAt: formData.expiresAt || null
      };

      const response = await pointsService.createCoupon(couponData);
      if (response.data.success) {
        setSuccess('Gutschein erfolgreich erstellt!');
        setFormData({ code: '', points: '', maxUses: '', expiresAt: '' });
        setShowCreateForm(false);
        loadCoupons();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Fehler beim Erstellen des Gutscheins');
    } finally {
      setFormLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Gutschein Verwaltung</h5>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Abbrechen' : '+ Neuer Gutschein'}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Neuen Gutschein erstellen</h6>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="code" className="form-label">Code *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="code"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="points" className="form-label">Punkte *</label>
                    <input
                      type="number"
                      className="form-control"
                      id="points"
                      name="points"
                      value={formData.points}
                      onChange={handleInputChange}
                      min="1"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="maxUses" className="form-label">Max. Verwendungen</label>
                    <input
                      type="number"
                      className="form-control"
                      id="maxUses"
                      name="maxUses"
                      value={formData.maxUses}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="Unbegrenzt"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="expiresAt" className="form-label">Ablaufdatum</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="expiresAt"
                      name="expiresAt"
                      value={formData.expiresAt}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={formLoading}>
                {formLoading ? 'Erstellen...' : 'Erstellen'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Coupons Table */}
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Code</th>
              <th>Punkte</th>
              <th>Verwendet</th>
              <th>Status</th>
              <th>Erstellt</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map(coupon => (
              <tr key={coupon._id}>
                <td><code>{coupon.code}</code></td>
                <td>{coupon.points}</td>
                <td>{coupon.usedBy.length}{coupon.maxUses ? `/${coupon.maxUses}` : ''}</td>
                <td>
                  {coupon.isAvailable ? (
                    <span className="badge bg-success">Verfügbar</span>
                  ) : (
                    <span className="badge bg-danger">Nicht verfügbar</span>
                  )}
                </td>
                <td>
                  {new Date(coupon.createdAt).toLocaleDateString('de-DE')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CouponManagement;
